import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  fetchTodayPuzzles,
  fetchMyToday,
  fetchMyStreaks,
  isLockedPuzzle,
  type Difficulty,
  type DifficultyDayState,
  type TodayPuzzle,
} from '../api/puzzles';
import { DifficultyCard } from '../components/landing/DifficultyCard';
import { SeasonStrip } from '../components/season/SeasonStrip';
import { LetterTiles } from '../components/ui/LetterTiles';
import { Seo } from '../components/Seo';
import { fetchCurrentSeason } from '../api/seasons';
import { useCountdown } from '../hooks/useCountdown';
import { loadLocalLife, guestDayState } from '../game/local-persistence';
import { isDayClaimed } from '../api/anon';
import { formatDateUK } from '../lib/format';

const LANDING_DESCRIPTION =
  'Play a free daily word puzzle. Everyone gets the same board — three ' +
  'difficulties, three lives, one bag of tiles. Beat the target score before ' +
  'your moves run out. New puzzle at midnight UK time.';

const LANDING_JSON_LD = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Scrabble Daily',
    url: 'https://scrabbledaily.com/',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Game',
    name: 'Scrabble Daily',
    url: 'https://scrabbledaily.com/',
    description: LANDING_DESCRIPTION,
    genre: 'Word puzzle',
    gamePlatform: 'Web browser',
    isAccessibleForFree: true,
  },
];

const DIFFICULTIES: Difficulty[] = ['EASY', 'MEDIUM', 'HARD'];

export default function Landing() {
  const { user, loading: authLoading } = useAuth();
  const countdown = useCountdown();

  // Guests read the public endpoint; signed-in players get per-difficulty state.
  const guestQuery = useQuery({
    queryKey: ['puzzles', 'today'],
    queryFn: fetchTodayPuzzles,
    enabled: !authLoading && !user,
  });
  const myTodayQuery = useQuery({
    queryKey: ['me', 'today'],
    queryFn: fetchMyToday,
    enabled: !authLoading && !!user,
  });
  const streaksQuery = useQuery({
    queryKey: ['me', 'streaks'],
    queryFn: fetchMyStreaks,
    enabled: !authLoading && !!user,
  });
  // Public endpoint (optionalAuth) — fetched once the access token is
  // settled so a signed-in player's `me` standing comes back attached.
  // Never allowed to block the boards below: a failed fetch renders nothing
  // (see SeasonStrip), not an error in place of the strip.
  const seasonQuery = useQuery({
    // Keyed by viewer: the SAME endpoint returns `me: null` to a guest and a
    // personal standing to a signed-in player. Under one shared key the
    // signed-out payload survives sign-in for the global 30s staleTime, so
    // the strip keeps saying "Finish a board to start your season" to someone
    // who just signed in with points already banked. Prefix-matching means
    // invalidateQueries(['seasons','current']) still clears every viewer's
    // copy.
    queryKey: ['seasons', 'current', user?.id ?? 'guest'],
    queryFn: fetchCurrentSeason,
    enabled: !authLoading,
  });

  const isLoading = authLoading || (user ? myTodayQuery.isLoading : guestQuery.isLoading);
  const isError = user ? myTodayQuery.isError : guestQuery.isError;

  const today = (user ? myTodayQuery.data?.date : guestQuery.data?.date) ?? null;
  // A claimed day's local save is a duplicate of what now lives on the
  // player's account. Showing it again once they sign out would present their
  // own account history back to them as guest progress, and any further play
  // on it could never be claimed — the day is already marked claimed.
  const claimed = today !== null && isDayClaimed(today);

  const days: DifficultyDayState[] = user
    ? (myTodayQuery.data?.difficulties ?? [])
    : DIFFICULTIES.map((difficulty) => {
        const puzzle: TodayPuzzle | null =
          guestQuery.data?.puzzles.find((p) => p.difficulty === difficulty) ?? null;
        // The server payload is the sole authority on locking (and already
        // reflects GUEST_FREE_BOARDS) — no local mirror override here.
        const locked = isLockedPuzzle(puzzle);
        // Guests track their day in localStorage (saved by the game screen).
        const local = guestDayState(
          puzzle && !isLockedPuzzle(puzzle) && !claimed ? loadLocalLife(puzzle.id) : null,
        );
        return {
          difficulty,
          ...local,
          locked,
          target_score: puzzle?.target_score ?? null,
          in_progress_life_id: null,
          puzzle,
        };
      });

  const anyLocked = days.some((d) => d.locked);
  // Signed out with today's run already on an account: guest play can't reach
  // the lives waiting there, so every card points at signing in instead.
  const claimedSignedOut = !user && claimed;

  const streaks = streaksQuery.data?.streaks ?? [];

  return (
    <section data-testid="page-landing">
      <Seo
        title="Scrabble Daily — Free Daily Word Puzzle"
        description={LANDING_DESCRIPTION}
        path="/"
        jsonLd={LANDING_JSON_LD}
      />
      <header className="pt-2 text-center">
        <h1 aria-label="Scrabble Daily">
          <LetterTiles word="SCRABBLE" size="lg" label="Scrabble" />
          <LetterTiles word="DAILY" size="lg" label="Daily" className="mt-1.5" />
        </h1>
        <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-stone-600">
          Three boards · three lives · one bag{today ? ` · ${formatDateUK(today)}` : ''}
        </p>
        {/* Static intro (no API dependency) so the prerendered HTML has real,
            keyword-relevant content for crawlers. */}
        <p className="mx-auto mt-3 max-w-md text-sm text-stone-600">
          A free daily word puzzle: place letter tiles crossword-style to beat the day's target
          score. Same three boards for everyone — easy, medium and hard — with three lives each.
        </p>
        <p
          aria-live="off"
          className="mx-auto mt-3 inline-flex items-baseline gap-2 rounded-md bg-stone-900 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400"
        >
          Next puzzle in
          <span className="font-mono text-sm font-bold normal-case tracking-widest text-amber-300">
            {countdown}
          </span>
        </p>
        {!authLoading && !user && !anyLocked && !claimed ? (
          <p className="mx-auto mt-3 max-w-md text-sm font-medium text-stone-700">
            One board free today — pick any. Create a free account to play all three.
          </p>
        ) : null}
      </header>

      {claimedSignedOut ? (
        // One explanation, above the boards, instead of the same prompt
        // repeated on every card. The boards stay unplayable on purpose: this
        // browser's guest day has already been handed to an account, and
        // handing it back on sign-out is exactly the loophole the guest gate
        // exists to close.
        <div
          data-testid="claimed-signed-out-banner"
          className="mt-6 rounded-lg border border-tile-edge bg-[#fffdf6] p-4 text-center shadow-[0_3px_0_rgba(217,199,154,0.9)]"
        >
          <p className="text-sm font-semibold text-stone-800">
            You're signed out. Today's boards are saved to your account.
          </p>
          <Link
            to="/login"
            className="mt-2.5 inline-block rounded-md border-b-4 border-emerald-950 bg-emerald-800 px-4 py-2 text-sm font-bold text-white"
          >
            Sign in to keep playing
          </Link>
        </div>
      ) : null}

      {!seasonQuery.isError && seasonQuery.data ? (
        <div className="mt-6">
          <SeasonStrip season={seasonQuery.data} signedIn={!!user} />
        </div>
      ) : null}

      {isLoading ? (
        <p className="mt-6 text-stone-500" role="status">
          Loading today's puzzles…
        </p>
      ) : isError ? (
        <p className="mt-6 text-red-600" role="alert">
          Couldn't load today's puzzles — check your connection and try again.
        </p>
      ) : (
        <div className="mt-6 space-y-4">
          {days.map((day) => (
            <DifficultyCard
              key={day.difficulty}
              day={day}
              streak={streaks.find((s) => s.difficulty === day.difficulty) ?? null}
              date={today}
              authed={!!user}
              claimedSignedOut={claimedSignedOut}
            />
          ))}
        </div>
      )}

      {/* Suppressed while claimedSignedOut: the banner above already asks them
          to sign in, and "save your streaks" is wrong when their streaks are
          already saved. */}
      {!authLoading && !user && !claimedSignedOut ? (
        <p className="mt-8 rounded-lg border border-tile-edge bg-[#fffdf6] p-4 text-center text-sm text-stone-700 shadow-[0_3px_0_rgba(217,199,154,0.9)]">
          <Link to="/login" className="font-bold text-emerald-800 underline">
            Sign in
          </Link>{' '}
          to save your streaks and history.
        </p>
      ) : null}
    </section>
  );
}
